import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import { CRM_ETAPAS, CRM_TIPO_LABELS, CRM_TIPO_TONES, type OportunidadeRow } from '../types';

interface PipelineProps {
  password: string;
  refreshKey: number;
  onChanged: () => void;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function Pipeline({ password, refreshKey, onChanged }: PipelineProps) {
  const [oportunidades, setOportunidades] = useState<OportunidadeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<number | null>(null);
  const [tipoFiltro, setTipoFiltro] = useState<'todos' | 'publico' | 'privado'>('todos');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'list_oportunidades', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as { oportunidades?: OportunidadeRow[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setOportunidades(parsed?.oportunidades ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar pipeline.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const move = async (oportunidadeId: number, etapa: string) => {
    setMoving(oportunidadeId);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'update_oportunidade', password, oportunidade_id: oportunidadeId, etapa },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao mover oportunidade.');
    } finally {
      setMoving(null);
    }
  };

  const filtradas =
    tipoFiltro === 'todos'
      ? oportunidades
      : oportunidades.filter((o) => (o.empresas?.tipo ?? 'privado') === tipoFiltro);

  const byEtapa = (etapa: string) => filtradas.filter((o) => o.etapa === etapa);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-filter-3-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Pipeline</h2>
            <p className="text-sm text-gray-500">{filtradas.length} oportunidades no total</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      <div className="inline-flex items-center bg-gray-100 rounded-full px-1 py-1">
        {([
          { key: 'todos', label: 'Todos', icon: 'ri-apps-2-line' },
          { key: 'privado', label: 'Privado', icon: 'ri-building-2-line' },
          { key: 'publico', label: 'Público · Licitações', icon: 'ri-government-line' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTipoFiltro(t.key)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors cursor-pointer whitespace-nowrap ${
              tipoFiltro === t.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={t.icon}></i>
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 items-start min-w-max">
          {CRM_ETAPAS.map((etapa) => {
            const cards = byEtapa(etapa.key);
            const total = cards.reduce((acc, o) => acc + Number(o.valor_potencial ?? 0), 0);
            return (
              <div key={etapa.key} className="w-60 shrink-0 bg-white rounded-xl border border-hop-border p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-2.5 py-1 ${etapa.tone}`}>
                    {etapa.label}
                    <span className="bg-white/60 rounded-full px-1.5 py-0.5 text-[10px]">{cards.length}</span>
                  </span>
                </div>
                {total > 0 && (
                  <p className="text-sm font-bold text-gray-900 mb-2">{formatBRL(total)}</p>
                )}
                <div className="space-y-2">
                  {cards.map((o) => (
                    <div key={o.id} className="p-3 bg-hop-bg-alt rounded-lg border border-hop-border">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {o.empresas?.nome ?? 'Sem empresa'}
                        </p>
                        <span className={`inline-flex items-center gap-1 text-[9px] font-semibold border rounded-full px-1.5 py-0.5 shrink-0 ${CRM_TIPO_TONES[o.empresas?.tipo === 'publico' ? 'publico' : 'privado'] ?? ''}`}>
                          <i className={o.empresas?.tipo === 'publico' ? 'ri-government-line' : 'ri-building-2-line'}></i>
                          {CRM_TIPO_LABELS[o.empresas?.tipo === 'publico' ? 'publico' : 'privado'] ?? 'Privado'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {o.empresas?.cidade ?? ''}
                        {o.empresas?.cidade && o.valor_potencial != null ? ' · ' : ''}
                        {o.valor_potencial != null ? formatBRL(o.valor_potencial) : ''}
                      </p>
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <span className="text-[10px] font-semibold text-gray-500">
                          {o.empresas?.score ?? o.score} pts
                        </span>
                        <select
                          value={o.etapa}
                          disabled={moving === o.id}
                          onChange={(e) => move(o.id, e.target.value)}
                          className="text-[11px] font-semibold text-gray-600 border border-gray-200 rounded-md px-1.5 py-0.5 bg-white cursor-pointer disabled:opacity-60 focus:outline-none"
                        >
                          {CRM_ETAPAS.map((e) => (
                            <option key={e.key} value={e.key}>{e.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-4">Vazio</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}